import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, MapPin, Clock, ChevronLeft, ChevronRight, Info, Lock, Images } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import QuantityStepper from "../components/QuantityStepper";
import OrderSummary from "../components/OrderSummary";
import AccommodationModal from "../components/AccommodationModal";
import CheckoutView from "../components/CheckoutView";
import WaitlistForm from "../components/WaitlistForm";
import { useToast } from "../hooks/use-toast";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gbp = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    n
  );

/* ---- Ticket / Accommodation Card ---- */
const ItemCard = ({ item, qty, onChange, disabled, thumb, onViewDetails }) => {
  const locked = item.soldOut || item.comingSoon;
  const capped = typeof item.remaining === "number";
  const atMax = capped && qty >= item.remaining;
  return (
    <div
      className={`flex gap-4 border rounded-md p-5 transition-colors duration-200 ${
        qty > 0
          ? "border-[#9b3d0a] bg-[#f3e7d6]"
          : "border-[#d9c3a8] bg-white"
      } ${locked ? "opacity-60" : ""}`}
    >
      {(thumb || item.image) && item.image && (
        onViewDetails ? (
          <button
            type="button"
            onClick={onViewDetails}
            className="w-24 h-24 shrink-0 rounded overflow-hidden hidden sm:block group/thumb relative"
          >
            <img
              src={item.image}
              alt={item.name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover/thumb:scale-105"
            />
          </button>
        ) : (
          <div className="w-24 h-24 shrink-0 rounded overflow-hidden hidden sm:block">
            <img
              src={item.image}
              alt={item.name}
              className="w-full h-full object-cover"
            />
          </div>
        )
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <h4 className="text-[#1e231f] font-semibold leading-snug">
            {item.name}
          </h4>
          <div className="text-right shrink-0">
            <p className="feral-serif text-[#9b3d0a] text-xl leading-none">
              {gbp(item.price)}
            </p>
            <p className="text-[#9a876f] text-xs mt-1">/ {item.unit}</p>
          </div>
        </div>
        {item.nights && (
          <p className="text-[#9b3d0a] text-xs font-semibold mt-1">
            {item.nights}
            {item.sleeps ? ` \u00b7 ${item.sleeps}` : ""}
          </p>
        )}
        {(item.desc || item.blurb) && (
          <p className="text-[#6e5646] text-sm mt-2 leading-relaxed">
            {item.desc || item.blurb}
          </p>
        )}

        {onViewDetails && (
          <button
            type="button"
            onClick={onViewDetails}
            className="inline-flex items-center gap-1.5 text-[#9b3d0a] hover:text-[#7e3208] text-sm font-semibold mt-2.5 hover:gap-2 transition-all"
          >
            <Images size={15} /> View room details &amp; photos
          </button>
        )}

        <div className="mt-4 flex items-center justify-between">
          {item.soldOut ? (
            <span className="inline-block bg-[#d9c3a8] text-[#9a876f] text-xs font-bold tracking-wide uppercase px-3 py-1.5 rounded">
              Sold out
            </span>
          ) : item.comingSoon ? (
            <span className="inline-block bg-[#f0dbbe] text-[#9b3d0a] text-xs font-bold tracking-wide uppercase px-3 py-1.5 rounded">
              Coming soon
            </span>
          ) : (
            <>
              <span className="text-xs text-[#9a876f] flex flex-col gap-0.5">
                {item.leftLabel ? (
                  <span className="text-[#9b3d0a] font-bold uppercase tracking-wide" data-testid={`left-${item.id}`}>{item.leftLabel}</span>
                ) : capped ? (
                  <span className="text-[#7e6a52] font-semibold" data-testid={`left-${item.id}`}>{item.remaining} left</span>
                ) : null}
                {qty > 0 && (
                  <span>{`${gbp(item.price * qty)} total`}</span>
                )}
                {atMax && (
                  <span className="text-[#9b3d0a] text-[11px]">Max available reached</span>
                )}
              </span>
              <QuantityStepper value={qty} onChange={onChange} disabled={disabled} testId={`qty-${item.id}`} max={capped ? item.remaining : undefined} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const SectionHeading = ({ step, title, sub }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3">
      <span className="w-8 h-8 rounded-full bg-[#9b3d0a] text-[#f2e5de] flex items-center justify-center font-semibold text-sm">
        {step}
      </span>
      <h3 className="feral-serif text-[#9b3d0a] text-3xl">{title}</h3>
    </div>
    {sub && <p className="text-[#6e5646] text-sm mt-2 ml-11">{sub}</p>}
  </div>
);

const EventBooking = ({ forceBooking = false }) => {
  const { slug: routeSlug } = useParams();
const slug = routeSlug === "autumn-equinox" ? "feral-fest" : routeSlug;
  const { toast } = useToast(); 
  const [event, setEvent] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`feral_cart_${slug}`)) || {};
    } catch (e) {
      return {};
    }
  });
  const [activeImg, setActiveImg] = useState(0);
  const thumbStripRef = useRef(null);

const scrollThumbnails = (direction) => {
  if (!thumbStripRef.current) return;

  thumbStripRef.current.scrollBy({
    left: direction === "left" ? -320 : 320,
    behavior: "smooth",
  });
};
  const [showCheckout, setShowCheckout] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  useEffect(() => {
    setLoadState("loading");
    axios
      .get(`${API}/events/${slug}`)
      .then((r) => {
        setEvent(r.data);
        setLoadState("ready");
      })
      .catch(() => setLoadState("notfound"));
  }, [slug]);

  // Persist cart so the basket stays full if they leave and come back
  useEffect(() => {
    try {
      localStorage.setItem(`feral_cart_${slug}`, JSON.stringify(cart));
    } catch (e) {
      /* ignore */
    }
    window.dispatchEvent(new Event("feral-cart-updated"));
  }, [cart, slug]);

  const feralFest = event || {};
  const waitlistMode = Boolean(event?.waitlistMode) && !forceBooking;
  const dayTickets = event?.dayTickets || [];
  const weekendTickets = event?.weekendTickets || [];
  const accommodation = (event?.accommodation || []).filter(
  (item) => !item.addOnOnly && !item.privateOnly
);

  const findItem = (id) =>
    [...dayTickets, ...weekendTickets, ...accommodation].find((i) => i.id === id);

  const setQty = (id, qty) =>
    setCart((c) => {
      const next = { ...c };
      if (qty <= 0) delete next[id];
      else next[id] = qty;
      return next;
    });

  const weekendCount = weekendTickets.reduce(
    (sum, t) => sum + (cart[t.id] || 0),
    0
  );
  const accommodationEnabled = weekendTickets.length === 0 || weekendCount > 0;

  // If weekend tickets removed, clear accommodation from cart
  useEffect(() => {
    if (!accommodationEnabled) {
      setCart((c) => {
        const next = { ...c };
        let changed = false;
        accommodation.forEach((a) => {
          if (next[a.id]) {
            delete next[a.id];
            changed = true;
          }
        });
        return changed ? next : c;
      });
    }
  }, [accommodationEnabled]); // eslint-disable-line

  const lines = useMemo(
    () =>
      Object.entries(cart)
        .map(([id, qty]) => {
          const item = findItem(id);
          if (!item) return null;
          return {
            id,
            qty,
            name: item.name,
            price: item.price,
            unit: item.unit,
            max: typeof item.remaining === "number" ? item.remaining : undefined,
          };
        })
        .filter(Boolean),
    [cart, event] // eslint-disable-line
  );

  const subtotal = lines.reduce((s, l) => s + l.price * l.qty, 0);

  // Clamp any cart quantities to the current remaining capacity
  useEffect(() => {
    if (loadState !== "ready") return;
    setCart((c) => {
      const next = { ...c };
      let changed = false;
      [...dayTickets, ...weekendTickets, ...accommodation].forEach((it) => {
        if (typeof it.remaining === "number" && (next[it.id] || 0) > it.remaining) {
          if (it.remaining <= 0) delete next[it.id];
          else next[it.id] = it.remaining;
          changed = true;
        }
      });
      return changed ? next : c;
    });
  }, [event]); // eslint-disable-line

  if (loadState !== "ready") {
    return (
      <div className="bg-[#f2e5de] min-h-screen">
        <Navbar tone="sunset" />
        <div className="feral-texture min-h-[60vh] flex items-center justify-center px-6 pt-28">
          <p className="text-[#6e5646]" data-testid="event-load-state">
            {loadState === "loading" ? "Loading event…" : "Event not found."}
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  if (showCheckout) {
    return (
      <CheckoutView
        event={slug}
        items={Object.entries(cart).map(([id, qty]) => ({ id, qty }))}
        eventName={feralFest.name}
        dateLine={feralFest.dateLine}
        lines={lines}
        total={subtotal}
        onBack={() => setShowCheckout(false)}
      />
    );
  }

  return (
    <div className="bg-[#f2e5de] min-h-screen">
      <Navbar tone="sunset" />

      {/* Event hero */}
      <section className="feral-texture pt-28 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/events"
            className="inline-flex items-center gap-1 text-[#9b3d0a] hover:gap-2 transition-all text-sm font-semibold mb-6"
          >
            <ChevronLeft size={18} /> All events
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            {/* Gallery */}
            <div>
              <div className="rounded-md overflow-hidden shadow-xl aspect-[3/2]">
                <img
                  src={(feralFest.images || [])[activeImg]}
                  alt={feralFest.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div
  className={`relative mt-3 ${
    (feralFest.images || []).length > 4 ? "px-7" : ""
  }`}
>
  {(feralFest.images || []).length > 4 && (
    <button
      type="button"
      onClick={() => scrollThumbnails("left")}
      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-[#f5eadc] border border-[#c9a77d] flex items-center justify-center text-[#9b3d0a] shadow-sm hover:bg-[#ead8c3] transition-colors"
      aria-label="Previous photos"
    >
      <ChevronLeft size={17} />
    </button>
  )}

  <div
    ref={thumbStripRef}
    className="flex gap-2 overflow-x-hidden scroll-smooth"
  >
    {(feralFest.images || []).map((img, i) => (
      <button
        key={i}
        type="button"
        onClick={() => setActiveImg(i)}
        className={`shrink-0 w-24 h-16 rounded-md overflow-hidden border-2 transition-all ${
          i === activeImg
            ? "border-[#9b3d0a] opacity-100"
            : "border-transparent opacity-70 hover:opacity-100"
        }`}
      >
        <img
          src={img}
          alt=""
          className="w-full h-full object-cover"
        />
      </button>
    ))}
  </div>

  {(feralFest.images || []).length > 4 && (
    <button
      type="button"
      onClick={() => scrollThumbnails("right")}
      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-[#f5eadc] border border-[#c9a77d] flex items-center justify-center text-[#9b3d0a] shadow-sm hover:bg-[#ead8c3] transition-colors"
      aria-label="Next photos"
    >
      <ChevronRight size={17} />
    </button>
  )}
</div>
            </div>

            {/* Details */}
            <div>
              {(feralFest.category || feralFest.subtitle) && (
                <p className="text-[#9b3d0a] tracking-[0.18em] text-xs font-semibold uppercase mb-3">
                  {feralFest.category || feralFest.subtitle}
                </p>
              )}
              <h1 className="feral-serif text-[#9b3d0a] text-5xl md:text-6xl mb-6">
                {feralFest.name}
              </h1>
              <div className="space-y-3 mb-6">
                {feralFest.dateLine && (
                  <div className="flex items-center gap-3 text-[#2e322c]">
                    <Calendar size={18} className="text-[#ebc754]" />
                    <span>{feralFest.dateLine}</span>
                  </div>
                )}
                {feralFest.time && (
                  <div className="flex items-center gap-3 text-[#2e322c]">
                    <Clock size={18} className="text-[#ebc754]" />
                    <span>{feralFest.time}</span>
                  </div>
                )}
                {feralFest.venue && (
                  <div className="flex items-center gap-3 text-[#2e322c]">
                    <MapPin size={18} className="text-[#ebc754]" />
                    <span>{feralFest.venue}</span>
                  </div>
                )}
              </div>

              {feralFest.priceFrom && (
                <p className="feral-serif text-[#9b3d0a] text-lg sm:text-2xl mb-6 leading-snug">
                  {feralFest.priceFrom}
                </p>
              )}

              {(feralFest.limitedNote || feralFest.accommodationNote || feralFest.ageNote) && (
                <div className="bg-[#f3e7d6] border border-[#d9c3a8] rounded-md p-4 space-y-2">
                  {feralFest.limitedNote && (
                    <p className="text-[#9b3d0a] font-semibold text-sm">
                      {feralFest.limitedNote}
                    </p>
                  )}
                  {feralFest.accommodationNote && (
                    <p className="text-[#6e5646] text-sm italic">
                      {feralFest.accommodationNote}
                    </p>
                  )}
                  {feralFest.ageNote && (
                    <p className="text-[#6e5646] text-sm italic">
                      {feralFest.ageNote}
                    </p>
                  )}
                </div>
              )}

              <a
                href="#book"
                data-testid="event-hero-cta"
                className="inline-block mt-6 bg-[#9b3d0a] hover:bg-[#7e3208] text-[#f2e5de] px-10 py-3.5 tracking-[0.15em] text-sm font-semibold transition-colors duration-300"
              >
                {waitlistMode ? "JOIN THE WAITLIST" : "BOOK NOW"}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Description + Method */}
      <section className="feral-texture px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-5 text-[#1e231f] text-lg leading-relaxed">
            {(feralFest.description || []).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {(feralFest.method || []).map((m) => (
              <div
                key={m.stage}
                className="bg-white border border-[#d9c3a8] rounded-md p-5"
              >
                <h4 className="text-[#9b3d0a] font-semibold tracking-[0.1em] uppercase text-sm mb-2">
                  {m.stage}
                </h4>
                <p className="text-[#6e5646] text-sm leading-relaxed">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking */}
      <section id="book" className="feral-texture bg-[#e2c7b0] py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {waitlistMode ? (
            <div className="max-w-xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl mb-3">
                  Join the waitlist
                </h2>
                {feralFest.limitedNote && (
                  <p className="text-[#6e5646] text-sm">{feralFest.limitedNote}</p>
                )}
              </div>
              <WaitlistForm slug={slug} eventName={feralFest.name} />
            </div>
          ) : (
          <>
          <div className="text-center mb-12">
            <p className="text-[#9b3d0a] tracking-[0.22em] text-sm font-semibold mb-3">
              GET YOUR TICKET
            </p>
            <h2 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl">
              Book your place
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
            {/* Selection */}
            <div className="lg:col-span-2 space-y-14">
              {/* Step 1 tickets */}
              <div>
                <SectionHeading
                  step="1"
                  title="Select your ticket"
                  sub={accommodation.length > 0 && weekendTickets.length > 0 ? "Come for one day, or the full weekend. A weekend ticket unlocks on-site accommodation." : undefined}
                />

                {dayTickets.length > 0 && (
                  <p className="text-[#9a876f] tracking-[0.14em] text-xs font-bold uppercase mb-4">
                    Day tickets
                  </p>
                )}
                <div className="space-y-4">
                  {dayTickets.map((t) => (
                    <ItemCard
                      key={t.id}
                      item={t}
                      qty={cart[t.id] || 0}
                      onChange={(q) => setQty(t.id, q)}
                    />
                  ))}
                </div>

                {weekendTickets.length > 0 && (
                  <>
                    <p className="text-[#9a876f] tracking-[0.14em] text-xs font-bold uppercase mb-4 mt-8">
                      Weekend tickets
                    </p>
                    <div className="space-y-4">
                      {weekendTickets.map((t) => (
                        <ItemCard
                          key={t.id}
                          item={t}
                          qty={cart[t.id] || 0}
                          onChange={(q) => setQty(t.id, q)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Step 2 accommodation */}
              {accommodation.length > 0 && (
              <div>
                <SectionHeading
                  step="2"
                  title="Add accommodation"
                  sub="Stay on-site for the full weekend. Optional add-on."
                />

                {!accommodationEnabled && (
                  <div className="flex items-start gap-3 bg-[#f0dbbe] border border-[#d9c3a8] rounded-md p-4 mb-5">
                    <Lock size={18} className="text-[#ebc754] mt-0.5 shrink-0" />
                    <p className="text-[#7e3208] text-sm">
                      Add a <span className="font-semibold">weekend ticket</span>{" "}
                      above to unlock accommodation. All stays must be booked
                      alongside a weekend ticket.
                    </p>
                  </div>
                )}

                {weekendTickets.length > 0 && accommodationEnabled && (
                  <div className="flex items-start gap-3 bg-[#e6f0e2] border border-[#c6dcbc] rounded-md p-4 mb-5">
                    <Info size={18} className="text-[#5b8049] mt-0.5 shrink-0" />
                    <p className="text-[#4c6b3d] text-sm">
                      Accommodation unlocked — choose your stay below.
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  {accommodation.map((a) => (
                    <ItemCard
                      key={a.id}
                      item={a}
                      thumb
                      qty={cart[a.id] || 0}
                      onChange={(q) => setQty(a.id, q)}
                      disabled={!accommodationEnabled}
                      onViewDetails={() => setDetailItem(a)}
                    />
                  ))}
                </div>
              </div>
              )}
            </div>

            {/* Summary */}
            <div className="lg:sticky lg:top-24">
              <OrderSummary
                lines={lines}
                subtotal={subtotal}
                onRemove={(id) => setQty(id, 0)}
                onQtyChange={(id, qty) => setQty(id, qty)}
                onCheckout={() => setShowCheckout(true)}
              />
            </div>
          </div>
          </>
          )}
        </div>
      </section>

      {/* Accommodation detail modal */}
      <AccommodationModal
        item={detailItem}
        open={detailItem !== null}
        onOpenChange={(o) => !o && setDetailItem(null)}
        qty={detailItem ? cart[detailItem.id] || 0 : 0}
        onChange={(q) => detailItem && setQty(detailItem.id, q)}
        disabled={!accommodationEnabled}
      />

      <Footer />
    </div>
  );
};

export default EventBooking;
